import { router } from "@/main";
import { GET } from "@/state/api";
import { POST, server } from "@/state/server";
import { AdminConfigZod } from "@/state/types";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useReducer, useState } from "react";
import { z } from "zod";

export const Route = createFileRoute("/admin/config/temporada")({
  component: RouteComponent,
  validateSearch: z.object({ id: z.coerce.number() }),
  loader: () => GET("/admin/config"),
});

function RouteComponent() {
  const data = Route.useLoaderData();

  const { id } = Route.useSearch();

  return <Temporada temporada={data.temporadas[id]} config={data} />;
}
type AdminTemporada = z.infer<typeof AdminConfigZod>["temporadas"][number];

function Temporada({
  temporada: temporadaOrig,
  config,
}: {
  temporada: AdminTemporada;
  config: z.infer<typeof AdminConfigZod>;
}) {
  const [temporada, updateTemporada] = useReducer(temporadaReducer, {
    ...temporadaOrig,
    fotografos: temporadaOrig.fotografos
      .filter((x) => x.numero)
      .sort((a, b) => a.numero - b.numero),
  });
  const [showLugares, setShowLugares] = useState(false);
  const [showFotografos, setShowFotografos] = useState(false);

  const { lugares, unsetLugares } = useMemo(() => {
    const lugares = [...temporada.lugares]
      .map((l) => ({ ...l, nombre: config.lugares[l.lugar_id] }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
    const setLugares = new Set(lugares.map((l) => l.lugar_id));
    const unsetLugares = Object.entries(config.lugares)
      .filter(([id]) => !setLugares.has(Number(id)))
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([lugar_id, nombre]) => ({ lugar_id, nombre }));
    return { lugares, unsetLugares };
  }, [config.lugares, temporada.lugares]);

  const unsetFotografos = useMemo(() => {
    const set = new Set(
      temporada.fotografos.filter((f) => f.numero).map((f) => f.fotografo_id)
    );
    return Object.values(config.fotografos)
      .filter((f) => !set.has(f.id))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [temporada.fotografos]);

  return (
    <>
      <div>
        Nombre:{" "}
        <input
          value={temporada.descripcion}
          onChange={(e) =>
            updateTemporada({ f: "descripcion", v: e.target.value })
          }
        />
      </div>
      <div>
        <input
          type="checkbox"
          checked={temporada.activo}
          onChange={(e) =>
            updateTemporada({ f: "activo", v: e.target.checked })
          }
        />{" "}
        Visible
      </div>
      <div>
        Preferencia:{" "}
        <input
          type="number"
          value={temporada.prioridad}
          step="any"
          onChange={(e) =>
            updateTemporada({ f: "prioridad", v: Number(e.target.value) })
          }
        />
      </div>
      <div>
        Fechas:{" "}
        <input
          type="date"
          value={temporada.desde}
          onChange={(e) => updateTemporada({ f: "desde", v: e.target.value })}
        />
        {" - "}
        <input
          type="date"
          value={temporada.hasta}
          onChange={(e) => updateTemporada({ f: "hasta", v: e.target.value })}
        />
      </div>
      <div>
        Valor de referencia:{" "}
        <input
          type="number"
          value={temporada.valor_ref}
          onChange={(e) =>
            updateTemporada({ f: "valor_ref", v: Number(e.target.value) })
          }
        />
      </div>
      <h4 onClick={() => setShowLugares(!showLugares)}>Lugares</h4>
      {showLugares && (
        <ul>
          {lugares.map((l) => (
            <li>
              {l.nombre}{" "}
              <FontAwesomeIcon
                icon={faTimesCircle}
                style={{ color: "red" }}
                onClick={() =>
                  updateTemporada({ f: "remove_lugar", lugar_id: l.lugar_id })
                }
              />
            </li>
          ))}
          {unsetLugares[0] && (
            <li>
              Agregar:{" "}
              <select
                value=""
                onChange={(e) =>
                  e.target.value &&
                  updateTemporada({
                    f: "add_lugar",
                    lugar_id: Number(e.target.value),
                  })
                }
              >
                <option />
                {unsetLugares.map((l) => (
                  <option key={l.lugar_id} value={l.lugar_id}>
                    {l.nombre}
                  </option>
                ))}
              </select>
            </li>
          )}
        </ul>
      )}
      <h4 onClick={() => setShowFotografos(!showFotografos)}>Fotografos</h4>
      {showFotografos && (
        <ul className="grid-list">
          {temporada.fotografos.map((f, i) => (
            <li key={f.fotografo_id}>
              <img
                src={`${server}/fotografo/${f.fotografo_id}.jpg`}
                style={{ width: 100, paddingBottom: 10 }}
              />{" "}
              <input
                type="number"
                value={f.numero}
                style={{ width: 50 }}
                onChange={(e) =>
                  updateTemporada({
                    f: "fotografo_numero",
                    i,
                    n: Number(e.target.value),
                  })
                }
              />{" "}
              {config.fotografos[f.fotografo_id].nombre}
            </li>
          ))}
          <li>
            Agregar:
            <select
              value=""
              onChange={(e) =>
                updateTemporada({
                  f: "add_fotografo",
                  id: Number(e.target.value),
                })
              }
            >
              <option />
              {unsetFotografos.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nombre} ({f.email})
                </option>
              ))}
            </select>
          </li>
        </ul>
      )}
      <div>
        <button
          onClick={() => {
            const body: z.infer<typeof PostZod> = {
              descripcion: temporada.descripcion,
              prioridad: temporada.prioridad,
              activo: temporada.activo,
              desde: temporada.desde,
              hasta: temporada.hasta,
              valor_ref: temporada.valor_ref,
              lugares: temporada.lugares.map((l) => ({
                name: config.lugares[l.lugar_id],
                shape: l.shape,
                coords: l.coords,
              })),
              fotografos: temporada.fotografos,
            };
            POST(`/admin/temporada/${temporada.id}`, body).then(() => {
              router.invalidate();
              router.navigate({ to: "/admin/config" });
            });
          }}
        >
          ENVIAR
        </button>
      </div>
    </>
  );
}

type UpdateTemporada =
  | { f: "descripcion" | "desde" | "hasta"; v: string }
  | { f: "activo"; v: boolean }
  | { f: "prioridad" | "valor_ref"; v: number }
  | { f: "remove_lugar"; lugar_id: number }
  | { f: "add_lugar"; lugar_id: number }
  | { f: "fotografo_numero"; i: number; n: number }
  | { f: "add_fotografo"; id: number };

function temporadaReducer(
  prevState: AdminTemporada,
  action: UpdateTemporada
): AdminTemporada {
  switch (action.f) {
    // UGH: si unificamos esto se pierde el typesafe
    case "descripcion":
      return { ...prevState, [action.f]: action.v };
    case "activo":
      return { ...prevState, [action.f]: action.v };
    case "prioridad":
      return { ...prevState, [action.f]: action.v };
    case "desde":
      return { ...prevState, [action.f]: action.v };
    case "hasta":
      return { ...prevState, [action.f]: action.v };
    case "valor_ref":
      return { ...prevState, [action.f]: action.v };
    case "remove_lugar":
      return {
        ...prevState,
        lugares: prevState.lugares.filter((x) => x.lugar_id != action.lugar_id),
      };
    case "add_lugar":
      return {
        ...prevState,
        lugares: [
          ...prevState.lugares,
          { lugar_id: action.lugar_id, shape: "", coords: "" },
        ],
      };
    case "fotografo_numero":
      return {
        ...prevState,
        fotografos: [
          ...prevState.fotografos.slice(0, action.i),
          { ...prevState.fotografos[action.i], numero: action.n },
          ...prevState.fotografos.slice(action.i + 1),
        ],
      };
    case "add_fotografo":
      return {
        ...prevState,
        fotografos: [
          ...prevState.fotografos.filter((f) => f.fotografo_id != action.id),
          {
            fotografo_id: action.id,
            numero:
              Math.max(...prevState.fotografos.map((f) => f.numero), 0) + 1,
          },
        ],
      };
  }
}

const PostZod = z.object({
  activo: z.boolean(),
  descripcion: z.string(),
  desde: z.string(),
  hasta: z.string(),
  prioridad: z.number(),
  valor_ref: z.number(),
  lugares: z.array(
    z.object({ name: z.string(), shape: z.string(), coords: z.string() })
  ),
  fotografos: z.array(
    z.object({ fotografo_id: z.number(), numero: z.number() })
  ),
});
